import React from "react";
import DOMPurify from "dompurify";
import { marked } from "marked";
import { FeedbackPanel } from "./FeedbackPanel";
import { pluralizeText } from "../../lib";

export default function TurnCard({
  turn,
  turnIndex,
  isStreaming,
  isHistorical,
  thinkingBoxRef,
}) {
  const hasAnswer = !!turn.answer;
  const hasSqlQueries = turn.sqlQueries.length > 0;
  const hasMetrics = !!turn.metrics;

  return (
    <div className="ai-assistant-modal__turn">
      <div className="ai-assistant-modal__user-bubble">{turn.query}</div>

      <div className="ai-assistant-modal__assistant-card">
        {(isStreaming || turn.thinkingText) && (
          <div className="ai-assistant-modal__thinking">
            <button
              type="button"
              className="ai-assistant-modal__thinking-toggle"
              onClick={turn.onToggleThinking}
            >
              <span>{turn.thinkingExpanded ? "▾" : "▸"}</span>
              <span>
                Thinking
                {turn.thinkingExpanded ? "..." : " (click to expand)"}
              </span>
            </button>
            {turn.thinkingExpanded && (
              <div
                className="ai-assistant-modal__thinking-box"
                ref={isStreaming ? thinkingBoxRef : undefined}
              >
                <pre>{turn.thinkingText || "Processing your query..."}</pre>
              </div>
            )}
          </div>
        )}

        {hasAnswer && (
          <>
            <div className="ai-assistant-modal__tabs">
              <button
                type="button"
                className={turn.activeTab === "answer" ? "active" : ""}
                onClick={() => turn.onSetActiveTab("answer")}
              >
                Answer
              </button>
              <button
                type="button"
                className={turn.activeTab === "results" ? "active" : ""}
                onClick={() => turn.onSetActiveTab("results")}
                disabled={!hasSqlQueries}
              >
                Queries ({turn.sqlQueries.length})
              </button>
              <button
                type="button"
                className={turn.activeTab === "metrics" ? "active" : ""}
                onClick={() => turn.onSetActiveTab("metrics")}
                disabled={!hasMetrics}
              >
                Metrics
              </button>
            </div>

            {turn.activeTab === "answer" && (
              <div className="ai-assistant-modal__panel">
                <div className="ai-assistant-modal__answer">
                  <span
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(
                        marked.parse(turn.answer || ""),
                      ),
                    }}
                  />

                  {isStreaming && (
                    <span className="ai-assistant-modal__typing-indicator">
                      <span />
                      <span />
                      <span />
                    </span>
                  )}
                </div>
              </div>
            )}

            {turn.activeTab === "results" && (
              <div className="ai-assistant-modal__panel">
                {hasSqlQueries ? (
                  turn.sqlQueries.map((queryObj, index) => {
                    const hasRows =
                      queryObj.results && queryObj.results.length > 0;
                    const headers = hasRows
                      ? getHeadersForSqlQuery(queryObj.results)
                      : [];
                    return (
                      <details
                        className="ai-assistant-modal__result-block"
                        key={`${turnIndex}-${queryObj.sql}-${index}`}
                        open
                      >
                        <summary className="ai-assistant-modal__chip">
                          Query {index + 1}
                          {hasRows
                            ? ` (${queryObj.results.length} ${pluralizeText("row", queryObj.results.length)})`
                            : ""}
                        </summary>
                        <pre className="ai-assistant-modal__sql-preview">
                          {queryObj.sql}
                        </pre>
                        {hasRows && (
                          <details
                            className="ai-assistant-modal__results-collapsible"
                            open
                          >
                            <summary className="ai-assistant-modal__results-toggle">
                              Results
                            </summary>
                            <div className="ai-assistant-modal__table-wrapper">
                              <table className="ai-assistant-modal__table">
                                <thead>
                                  <tr>
                                    {headers.map((header) => (
                                      <th key={header}>{header}</th>
                                    ))}
                                  </tr>
                                </thead>
                                <tbody>
                                  {queryObj.results.map((row, rowIndex) => (
                                    <tr
                                      key={`${turnIndex}-${index}-${rowIndex}`}
                                    >
                                      {headers.map((header) => (
                                        <td key={`${header}-${rowIndex}`}>
                                          {String(row[header] ?? "")}
                                        </td>
                                      ))}
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </details>
                        )}
                      </details>
                    );
                  })
                ) : (
                  <div className="ai-assistant-modal__empty">
                    No SQL queries to display.
                  </div>
                )}
              </div>
            )}

            {turn.activeTab === "answer" &&
              !isStreaming &&
              !isHistorical &&
              turn.conversationId != null && (
                <FeedbackPanel
                  feedback={turn.feedback}
                  onSetFeedbackDraft={turn.onSetFeedbackDraft}
                  onSubmitFeedback={turn.onSubmitFeedback}
                />
              )}

            {turn.activeTab === "metrics" && hasMetrics && (
              <div className="ai-assistant-modal__panel">
                {turn.metrics.cancelled && (
                  <div className="ai-assistant-modal__metrics-note">
                    <em>Estimated due to cancellation</em>
                  </div>
                )}
                <div className="ai-assistant-modal__metrics">
                  <div className="ai-assistant-modal__metrics-row">
                    <span className="ai-assistant-modal__metrics-label">
                      Asked By
                    </span>
                    <span className="ai-assistant-modal__metrics-value">
                      {turn.metrics.askedBy || "—"}
                    </span>
                  </div>
                  <div className="ai-assistant-modal__metrics-row">
                    <span className="ai-assistant-modal__metrics-label">
                      Tool Rounds
                    </span>
                    <span className="ai-assistant-modal__metrics-value">
                      {turn.metrics.numToolRounds}
                    </span>
                  </div>
                  <div className="ai-assistant-modal__metrics-row">
                    <span className="ai-assistant-modal__metrics-label">
                      Duration
                    </span>
                    <span className="ai-assistant-modal__metrics-value">
                      {turn.metrics.durationSeconds.toFixed(1)}s
                    </span>
                  </div>
                  <div className="ai-assistant-modal__metrics-row">
                    <span className="ai-assistant-modal__metrics-label">
                      Input Tokens
                    </span>
                    <span className="ai-assistant-modal__metrics-value">
                      {turn.metrics.inputTokens.toLocaleString()}
                    </span>
                  </div>
                  <div className="ai-assistant-modal__metrics-row">
                    <span className="ai-assistant-modal__metrics-label">
                      Output Tokens
                    </span>
                    <span className="ai-assistant-modal__metrics-value">
                      {turn.metrics.outputTokens.toLocaleString()}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {isStreaming && !hasAnswer && !turn.thinkingText && (
          <div className="ai-assistant-modal__answer">
            <span className="ai-assistant-modal__typing-indicator">
              <span />
              <span />
              <span />
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

function getHeadersForSqlQuery(results) {
  if (!results || results.length === 0) {
    return [];
  }

  return Object.keys(results[0]);
}
